import { FC, useEffect, useState } from 'react';
import { ScreenSkeleton } from '../../../components/atoms/ScreenSkeleton';
import { SectionHeading } from '../../../components/atoms/SectionHeading';
import { Label } from '../../../components/atoms/Label';
import { useNavigate } from 'react-router';
import { ScreenHeading } from '../../../components/molecules/ScreenHeading';
import {
  Button,
  ButtonSameWidthContainer,
} from '../../../components/atoms/Button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import DropdownField from '../../../components/atoms/DropdownField';
import { SingleLineTextField } from '../../../components/atoms/SingleLineTextField';
import { ScreenObservationLayout } from '../../../components/templates/ScreenObservationLayout';
import { ObservableLevelsEnum } from '../../../data/enums';
import { ErrorBanner } from '../../../components/atoms/ErrorBanner';
import { DateField } from '../../../components/atoms/DateField';
import { useCustomerSitesAssets } from '../../../hooks/useCustomerSitesAssets';
import { v4 } from 'uuid';
import useGETFitments from '../../../hooks/useGETFitments';
import {
  AssetManufacturerModel,
  AssetModel,
  EquipmentModel,
  EquipmentPartModel,
  MineLocationModel,
  PartManufacturerModel,
} from '../../../data/types';
import { inspectionDB } from '../../../data/database.structure';
import { useAtom } from 'jotai';
import { createNewInspection } from '../../../store/newInspection/newInspectionAtoms';
import { session } from '../../../services/auth/session';
import useMergedState from '../../../hooks/useMergedState';
import useBucketSpecificationValidators from '../../../validators/useBucketSpecificationValidators';
import { equipmentService } from '../../../services/equipment/equipment.service';
import { inspectionRecordService } from '../../../services/inspection/inspectionRecordService';
import DatabaseAssetsMethods from '../../../data/database.assets.methods';
import DatabaseMineLocationMethods from '../../../data/database.minelocations.methods';
import { buildNoLaydownYardCreationMessage } from '../../../helpers/equipmentAssignmentHelpers';
import { toast } from '../../../store/toast';
import { EToastType } from '../../../components/molecules/ToastMessage';
import { WarningBanner } from '../../../components/atoms/WarningBanner';
import { LoadingIndicator } from '../../../components/atoms/LoadingIndicator';
import DateHelpers from '../../../helpers/dateHelpers';

const NewBucketConstants = {
  ITEMS_TO_DISPLAY: 10000,
  Title: 'New bucket',
  Subtitle: 'Bucket specifications',
};

interface FormData {
  customer?: {
    id: number;
    name: string;
  };
  site?: {
    id: number;
    name: string;
  };
  machine?: {
    id: number;
    name: string;
  };
  mineLocation?: {
    id: number;
    name: string;
  };
  bucketName?: string;
  manufacturer?: {
    id: number;
    name: string;
  };
  bucketPartNumber?: string;
  getFitment?: {
    id: number;
    name: string;
    manufacturerId: number;
  };
  bucketCINumber?: string;
  serialNumber?: string;
  installDate?: Date;
  machineHours?: number;
}

export const NewBlankBucket: FC<{}> = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useMergedState<FormData>({});

  const [error, setError] = useState<string | null>(null);
  const [isCreatingBucket, setIsCreatingBucket] = useState<boolean>(false);

  // #10363: a new bucket on a machine that already has one needs a laydown yard
  // at the site to move the current bucket to. Checked as soon as a machine is
  // picked so the inspector is told before filling in the rest of the form.
  const [noLaydownYardMessage, setNoLaydownYardMessage] = useState<
    string | null
  >(null);
  const selectedMachineId = formData?.machine?.id;

  useEffect(() => {
    if (selectedMachineId === undefined) {
      setNoLaydownYardMessage(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const asset = await new DatabaseAssetsMethods(inspectionDB).getAssetById({
        assetId: selectedMachineId,
      });
      const currentBucket = asset?.equipments.find(
        (e) =>
          e.equipmentCategory.toLowerCase() === 'bucket' &&
          e.equipmentType.toLowerCase() === 'bucket' &&
          e.id > 0,
      );
      if (!asset || !currentBucket) {
        if (!cancelled) setNoLaydownYardMessage(null);
        return;
      }
      const hasYard = await new DatabaseMineLocationMethods(
        inspectionDB,
      ).siteHasLaydownYard(asset.siteId);
      if (!cancelled)
        setNoLaydownYardMessage(
          hasYard
            ? null
            : buildNoLaydownYardCreationMessage(currentBucket.name),
        );
    })().catch((e) => {
      console.error(
        'Failed to check laydown yards for machine',
        selectedMachineId,
        e,
      );
      if (!cancelled)
        setNoLaydownYardMessage(buildNoLaydownYardCreationMessage());
    });
    return () => {
      cancelled = true;
    };
  }, [selectedMachineId]);

  const getFitements = useGETFitments();

  const { state } = useCustomerSitesAssets({
    customerId: formData?.customer?.id,
    siteId: formData?.site?.id,
  });

  const [{ mutateAsync }] = useAtom(createNewInspection);

  const { errors, validate, onChange } = useBucketSpecificationValidators<
    | 'GETFitment'
    | 'customerBucketName'
    | 'customers'
    | 'sites'
    | 'machineHours'
    | 'machine'
    | 'mineLocation'
  >({
    initialForm: {
      GETFitment: { required: true, errorMessage: 'GET Fitment is required' },
      customerBucketName: {
        required: true,
        errorMessage: 'Customer bucket name is required',
      },
      customers: { required: true, errorMessage: 'Customer is required' },
      sites: { required: true, errorMessage: 'Site is required' },
      machineHours: {
        required: true,
        errorMessage: 'Machine Hours is required',
      },
      machine: {
        required: false,
        errorMessage: 'Machine or Mine location is required',
      },
      mineLocation: {
        required: false,
        errorMessage: 'Machine or Mine location is required',
      },
    },
    either: [['machine', 'mineLocation']],
  });

  async function getAssetOrMineLocation(): Promise<{
    mineLocation: MineLocationModel | undefined;
    asset: AssetModel | undefined;
  }> {
    let asset: AssetModel | undefined = undefined;
    let mineLocation: MineLocationModel | undefined = undefined;
    if (formData?.machine !== undefined) {
      asset = await new DatabaseAssetsMethods(inspectionDB).getAssetById({
        assetId: formData?.machine?.id ?? -1,
        effectiveDate: formData?.installDate ?? new Date(),
      });

      if (!asset) {
        setError('Asset not found');
        return { mineLocation, asset };
      }
    } else if (formData?.mineLocation !== undefined) {
      mineLocation = await inspectionDB.mineLocations
        .where('id')
        .equals(formData?.mineLocation?.id ?? -1)
        .first();
      if (!mineLocation) {
        setError('Mine location not found');
        return { mineLocation, asset };
      }
    } else {
      setError('Machine or Mine location not selected');
      return { mineLocation, asset };
    }
    return { mineLocation, asset };
  }

  if (state.isLoading) {
    return <ScreenSkeleton fields={4} />;
  }

  if (state.error) {
    return <ErrorBanner message={state.error} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <>
      <div className="w-full bg-white">
        <ScreenHeading
          eyebrow={NewBucketConstants.Title ?? ''}
          title={NewBucketConstants.Subtitle}
          tooltipKey={'undercarriage'}
        />

        <ScreenObservationLayout className="">
          <SectionHeading className="mt-4" title="Location of bucket" />

          <section>
            <Label className="mt-4">Customers *</Label>
            <DropdownField
              dataLevel={ObservableLevelsEnum.equipment}
              inspectionId={''}
              entityId={-1}
              entityExternalId=""
              inspectionCategoryTypeId={''}
              path={''}
              options={state.customers.map((c, _) => c.name)}
              placeholder={'Select customer'}
              alwaysRunOnChange={true}
              value={formData?.customer?.name}
              onChange={(e) => {
                const customer = state.customers.find((c) => c.name === e);

                if (customer) {
                  setFormData({
                    customer: {
                      id: customer.id,
                      name: customer.name,
                    },
                    site: undefined,
                    machine: undefined,
                    mineLocation: undefined,
                  });
                  onChange?.('customers', e);
                } else {
                  setFormData({
                    customer: undefined,
                    site: undefined,
                    machine: undefined,
                    mineLocation: undefined,
                  });
                  onChange?.('customers', undefined);
                }
              }}
              error={errors.customers}
            />
          </section>
          <section>
            <Label className="mt-4">Site *</Label>
            <DropdownField
              dataLevel={ObservableLevelsEnum.equipment}
              inspectionId={''}
              entityId={-1}
              entityExternalId=""
              inspectionCategoryTypeId={''}
              path={''}
              options={state.sites.map((s, _) => s.name)}
              placeholder={'Select site'}
              alwaysRunOnChange={true}
              value={formData?.site?.name}
              onChange={(e) => {
                const site = state.sites.find((s) => s.name === e);
                if (site) {
                  setFormData({
                    site: {
                      id: site.id,
                      name: site.name,
                    },
                    machine: undefined,
                    mineLocation: undefined,
                  });
                  onChange?.('sites', e);
                } else {
                  setFormData({
                    site: undefined,
                    machine: undefined,
                    mineLocation: undefined,
                  });
                  onChange?.('sites', undefined);
                }
              }}
              error={errors.sites}
            />
          </section>

          <WarningBanner message='Select "Machine" or "Mine location" to create a new bucket.' />

          <Label>Machine *</Label>
          <DropdownField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            placeholder={'Select machine'}
            options={state.assets.map((a) => a.name) ?? []}
            alwaysRunOnChange={true}
            value={formData?.machine?.name}
            onChange={(e) => {
              const asset = state.assets.find((a) => a.name == e);
              if (asset) {
                setFormData({
                  machine: {
                    id: asset.id,
                    name: asset.name,
                  },
                  mineLocation: undefined,
                });
                onChange?.('machine', e);
              } else {
                setFormData({
                  machine: undefined,
                  mineLocation: undefined,
                });
                onChange?.('machine', undefined);
              }
            }}
            error={errors.machine}
          />

          {noLaydownYardMessage && (
            <WarningBanner showIcon message={noLaydownYardMessage} />
          )}

          <Label className="mt-4">Mine Location *</Label>
          <DropdownField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            placeholder={'Select mine location'}
            options={state.mineLocations.map((a) => a.name) ?? []}
            alwaysRunOnChange={true}
            value={formData?.mineLocation?.name}
            onChange={(e) => {
              const asset = state.mineLocations.find((a) => a.name == e);
              if (asset) {
                setFormData({
                  mineLocation: {
                    id: asset.id,
                    name: asset.name,
                  },
                  machine: undefined,
                });
                onChange?.('mineLocation', e);
              } else {
                setFormData({
                  mineLocation: undefined,
                  machine: undefined,
                });
                onChange?.('mineLocation', undefined);
              }
            }}
            error={errors.mineLocation}
          />

          <section>
            <SectionHeading className="mt-4" title="Bucket specifications" />
            <Label
              className="mt-4"
              tooltipKey={'bucket-new-blank-bucket-customer-bucket-name'}
            >
              Customer bucket name *
            </Label>
            <SingleLineTextField
              dataLevel={ObservableLevelsEnum.equipment}
              inspectionId={''}
              entityId={-1}
              entityExternalId=""
              inspectionCategoryTypeId={''}
              path={''}
              alwaysRunOnBlur={true}
              onChange={(e) => {
                setFormData({
                  bucketName: e,
                });
                onChange?.('customerBucketName', e);
              }}
              placeholder="Enter bucket name"
              type="string"
              error={errors.customerBucketName}
            />
          </section>

          <section>
            <Label
              className="mt-4"
              tooltipKey={'bucket-new-blank-bucket-manufacturer'}
            >
              Manufacturer
            </Label>
            <DropdownField
              dataLevel={ObservableLevelsEnum.equipment}
              inspectionId={''}
              entityId={-1}
              entityExternalId=""
              inspectionCategoryTypeId={''}
              path={''}
              options={state.manufacturers.map((m) => m.name)}
              placeholder="Select manufacturer"
              alwaysRunOnChange={true}
              value={formData?.manufacturer?.name}
              onChange={(e) => {
                const man = state.manufacturers.find((m) => m.name == e);
                if (man) {
                  setFormData({
                    manufacturer: {
                      id: man.id,
                      name: man.name,
                    },
                  });
                }
              }}
            />
          </section>

          <Label
            className="mt-4"
            tooltipKey={'bucket-new-blank-bucket-part-number'}
          >
            Bucket part number (P/N)
          </Label>
          <SingleLineTextField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            placeholder="Enter part number"
            type="string"
            alwaysRunOnBlur={true}
            onChange={(e) => {
              setFormData({
                bucketPartNumber: e,
              });
            }}
          />

          <Label
            className="mt-4"
            tooltipKey={'bucket-new-blank-bucket-get-fitment'}
          >
            GET Fitment *
          </Label>
          <DropdownField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            alwaysRunOnChange={true}
            options={getFitements.map((f) => f.name)}
            onChange={(e) => {
              const fitment = getFitements.find((f) => f.name === e);
              if (fitment) {
                setFormData({
                  getFitment: {
                    id: fitment.id,
                    name: fitment.name,
                    manufacturerId: fitment.manufacturerId,
                  },
                });
                onChange?.('GETFitment', e);
              } else {
                setFormData({
                  getFitment: undefined,
                });
                onChange?.('GETFitment', undefined);
              }
            }}
            placeholder={'Select GET Fitment'}
            error={errors.GETFitment}
          />

          <Label
            className="mt-4"
            tooltipKey={'bucket-new-blank-bucket-ci-number'}
          >
            Bradken Capital Item (CI) Number
          </Label>
          <SingleLineTextField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            placeholder="Enter CI Number"
            type="string"
            alwaysRunOnBlur={true}
            onChange={(e) => {
              setFormData({
                bucketCINumber: e,
              });
            }}
          />

          <Label
            className="mt-4"
            tooltipKey={'bucket-new-blank-bucket-serial-number'}
          >
            Customer's Bucket Serial Number
          </Label>
          <SingleLineTextField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            placeholder="Enter Serial Number"
            type="string"
            alwaysRunOnBlur={true}
            onChange={(e) => {
              setFormData({
                serialNumber: e,
              });
            }}
          />

          <SectionHeading className="mt-4" title="Installation on Machine" />
          <section className="space-y-2">
            <Label
              className="mt-4"
              tooltipKey={'bucket-new-blank-bucket-install-date'}
            >
              Install Date
            </Label>
            <DateField
              dataLevel={ObservableLevelsEnum.equipment}
              inspectionId={''}
              entityId={-1}
              entityExternalId=""
              inspectionCategoryTypeId={''}
              path={''}
              onChange={(d) => {
                setFormData({
                  installDate: d,
                });
              }}
              useUTC={true}
              value={formData.installDate}
            />
          </section>

          <Label
            className="mt-4"
            tooltipKey={'bucket-new-blank-bucket-machine-hours'}
          >
            Machine Hours *
          </Label>
          <SingleLineTextField
            dataLevel={ObservableLevelsEnum.equipment}
            inspectionId={''}
            entityId={-1}
            entityExternalId=""
            inspectionCategoryTypeId={''}
            path={''}
            onChange={(value) => {
              setFormData({
                machineHours: Number(value),
              });
              onChange?.('machineHours', value);
            }}
            type="integer"
            error={errors.machineHours}
            alwaysRunOnBlur={true}
          />

          <ButtonSameWidthContainer>
            <Button
              suffixIcon={<ArrowRight size={24} />}
              variant="primary"
              disabled={noLaydownYardMessage !== null || isCreatingBucket}
              onClick={async () => {
                if (!validate()) {
                  return;
                }

                // Each tap mints a new bucket id, so a second tap while the first is
                // still writing would create a second bucket and Inspection (#11627).
                // React applies a click's state update before the next click, so the
                // disabled button blocks it, as in NewBucket.
                setIsCreatingBucket(true);
                try {
                  const bucketId = v4();

                  const getFitment = await inspectionDB.equipmentConfigurations
                    .where('name')
                    .equals(formData?.getFitment?.name ?? '')
                    .first();
                  const getFitmentParts =
                    await inspectionDB.equipmentPartConfigurations
                      .where('configurationId')
                      .equals(getFitment?.id ?? -1)
                      .toArray();
                  const partIds = getFitmentParts
                    .map((part) => part.partId)
                    .filter((part) => part != undefined);
                  const parts = await inspectionDB.parts
                    .where('id')
                    .anyOf(partIds)
                    .toArray();
                  const partLocations =
                    await inspectionDB.partLocations.toArray();

                  const equipmentParts = getFitmentParts
                    .map((part) => {
                      const partModel = parts.find((p) => p.id === part.partId);
                      if (!partModel) {
                        return null;
                      }
                      const partLocation = partLocations.find(
                        (l) => l.id === part.locationId,
                      );
                      return {
                        id: 0,
                        externalId: v4(),
                        equipmentId: 0,
                        externalEquipmentId: bucketId,
                        partId: part.partId ?? -1,
                        locationId: part.locationId ?? -1,
                        position: part.position ?? -1,
                        secondaryLocationId: part.secondaryLocationId ?? -1,
                        locationName: partLocation?.name ?? 'Unknown',
                        part: partModel,
                        effectiveDate:
                          formData?.installDate ??
                          DateHelpers.convertLocalDateToUTCDate(new Date()),
                      };
                    })
                    .filter((part) => part !== null) as EquipmentPartModel[];

                  const manufacturers =
                    await inspectionDB.assetManufacturers.toArray();

                  let manufacturer: AssetManufacturerModel | undefined =
                    undefined;

                  if (
                    formData?.manufacturer?.id !== undefined &&
                    formData.manufacturer.id > 0
                  ) {
                    manufacturer = manufacturers.find(
                      (m) => m.id === formData?.manufacturer?.id,
                    );
                  } else if (
                    formData?.getFitment?.manufacturerId !== undefined &&
                    formData.getFitment.manufacturerId > 0
                  ) {
                    manufacturer = manufacturers.find(
                      (m) => m.id === formData?.getFitment?.manufacturerId,
                    );
                  } else {
                    toast.add({
                      message: 'Manufacturer not found',
                      type: EToastType.alert,
                      isOpen: true,
                    });
                    return;
                  }

                  let oldBucket: EquipmentModel | undefined = undefined;

                  const { mineLocation, asset } =
                    await getAssetOrMineLocation();
                  if (!mineLocation && !asset) {
                    setError('Machine or Mine location not selected');
                    return;
                  }

                  // If the new bucket is being assigned to an existing machine, check if there is a bucket on the machine
                  if (asset) {
                    oldBucket = asset.equipments.find(
                      (e) =>
                        e.equipmentCategory.toLowerCase() === 'bucket' &&
                        e.equipmentType.toLowerCase() === 'bucket' &&
                        e.id > 0,
                    );
                  }

                  // #10363: belt-and-braces — the outgoing bucket needs a laydown yard to move to.
                  if (asset && oldBucket) {
                    const siteHasLaydownYard =
                      await new DatabaseMineLocationMethods(
                        inspectionDB,
                      ).siteHasLaydownYard(asset.siteId);
                    if (!siteHasLaydownYard) {
                      setNoLaydownYardMessage(
                        buildNoLaydownYardCreationMessage(oldBucket.name),
                      );
                      return;
                    }
                  }

                  // If there is a Bucket on the machine, register as a changeout
                  // There is no bucket on the machine, create a new inspection

                  const inspectionId = v4();
                  try {
                    await equipmentService.createBucket({
                      bucketExternalId: bucketId,
                      equipmentConfiguration: formData.getFitment?.id ?? -1,
                      equipmentParts,
                      installDate: formData.installDate ?? new Date(),
                      effectiveDate: formData.installDate
                        ? formData.installDate
                        : DateHelpers.convertLocalDateToUTCDate(new Date()),
                      formData: {
                        bucketName: formData?.bucketName ?? '',
                        bucketCINumber: formData?.bucketCINumber ?? '',
                        bucketPartNumber: formData?.bucketPartNumber ?? '',
                        serialNumber: formData?.serialNumber ?? '',
                        machine: formData?.machine?.id ?? -1,
                        mineLocation: formData?.mineLocation?.id ?? -1,
                        manufacturer: manufacturer?.id ?? -1,
                      },
                    });

                    // Create Inspection

                    const inspectorName = session.claims()?.name;
                    if (!inspectorName) {
                      throw new Error(
                        'No inspector name in the stored sign-in',
                      );
                    }

                    const inspection = await mutateAsync({
                      inspectionId: inspectionId,
                      inspectorName: inspectorName,
                      assetId: asset?.id,
                      siteId:
                        asset !== undefined
                          ? asset.siteId
                          : (mineLocation?.siteId ?? -1),
                      mineLocationId: mineLocation?.id,
                      equipmentExternalId: bucketId,
                    });

                    // #10285: NO purge here, deliberately. The ticket names this screen
                    // alongside NewBucket, but it cannot carry the defect: `mutateAsync` above
                    // creates a brand-new inspection under a fresh `v4()` id two statements
                    // earlier, so there are never prior `Bucket.*` rows to remove. Calling
                    // `purgeBucketObservations` here only opened a Dexie transaction and read
                    // the inspection back to delete nothing. If an outgoing bucket does have
                    // observations they belong to the asset's OTHER, pre-existing inspection,
                    // which this flow does not and must not touch.

                    await equipmentService.createNewBucketObservations({
                      inspectionId,
                      inspectionCategoryId:
                        inspection?.inspectionCategoryTypes[0].externalId,
                      mineLocationId: mineLocation?.id,
                      mineLocationExternalId: mineLocation?.externalId,
                      assetId: asset?.id,
                      assetExternalId: asset?.externalId,
                      equipmentId: -1,
                      equipmentExternalId: bucketId,
                      oldBucketId: oldBucket?.externalId,
                      // A skipped write must reach the rollback below, or the
                      // Inspection is kept without its Bucket.* rows.
                      throwOnFailedWrite: true,
                      formData: {
                        bucketExternalId: bucketId,
                        bucketCapitalIndexNumber:
                          formData?.bucketCINumber ?? '',
                        bucketPartNumber: formData?.bucketPartNumber ?? '',
                        bucketSerialNumber: formData?.serialNumber ?? '',
                        bucketInstallDate: DateHelpers.toIsoDateString(
                          formData?.installDate ?? inspection.inspectionDate,
                        ),
                        bucketFitment: formData?.getFitment?.id ?? -1,
                        bucketManufacturer: manufacturer?.name ?? 'Unknown',
                        bucketName: formData?.bucketName ?? '',
                        bucketMachineHours: formData?.machineHours ?? -1,
                      },
                    });

                    // TODO: need to set current inspection
                    console.log(`created inspectionId: ${inspectionId}`);
                    navigate(`/inspections/${inspectionId}`, { replace: true });
                  } catch (e) {
                    console.error('Error creating bucket:', e);

                    // Both ids are fresh, so deleting by them only ever removes what this
                    // tap wrote, and is a no-op for anything it did not get to. One
                    // transaction, so the Inspection never outlives its bucket.
                    let undone = true;
                    try {
                      await inspectionDB.transaction(
                        'rw',
                        [
                          inspectionDB.inspections,
                          inspectionDB.inspectionBackups,
                          inspectionDB.equipments,
                          inspectionDB.equipmentParts,
                        ],
                        async () => {
                          await inspectionRecordService.delete(inspectionId);
                          await equipmentService.deleteLocalBucket(bucketId);
                        },
                      );
                    } catch (rollbackError) {
                      undone = false;
                      console.error(
                        'Rollback after a failed bucket create failed:',
                        rollbackError,
                      );
                    }

                    toast.add({
                      message: undone
                        ? 'Creating the bucket failed. Please try again.'
                        : 'Creating the bucket failed and could not be undone. Check Home for a new Inspection before trying again.',
                      type: EToastType.alert,
                      isOpen: true,
                    });
                  }
                } catch (e) {
                  // The reads before anything is written.
                  console.error('Error preparing the bucket:', e);
                  toast.add({
                    message: 'Creating the bucket failed. Please try again.',
                    type: EToastType.alert,
                    isOpen: true,
                  });
                } finally {
                  setIsCreatingBucket(false);
                }
              }}
            >
              CREATE
              {isCreatingBucket ? (
                <LoadingIndicator isLoading type="small" />
              ) : (
                <></>
              )}
            </Button>
            <Button
              prefixIcon={<ArrowLeft size={24} />}
              variant="secondary"
              disabled={isCreatingBucket}
              onClick={() => navigate(-1)}
            >
              CANCEL
            </Button>
          </ButtonSameWidthContainer>
        </ScreenObservationLayout>
      </div>
    </>
  );
};
